// Used in activity-builder.component.html

// Angular
import { KeyValue } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers";
import { LanguageHelper } from "#helpers/language.helper";
import { LocalStorage } from "#services/local-storage.service";
import { AssetId, EditionId, SubjectId } from "@carnegie-learning/common-types";
import { AssetAssociation, Edition, Level, Perform, PerformMedia, assetTypeIds } from "@carnegie-learning/cure-types";

@Component({
    selector: "avenue-form",
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./avenue-form.component.html",
    styleUrls: [
        "../../../../../styles/shared-styles.scss",
        "../../../../../styles/form-styles.scss",
    ],
    standalone: false
})
export class AvenueActivityFormComponent implements OnChanges, OnInit {
    exists = false;
    componentInit = false;
    activeProgram?: Edition;
    selectedEdition?: AssetAssociation;
    externalId = "";
    performAssetId: AssetId = "";
    selectedAssetID: AssetId = "";

    @Input() isDistributor = false;
    @Input() userGUID?: string;
    @Input() activePrograms: Edition[] = [];
    @Input() activeEditions: Edition[] = [];
    @Input() subjectId: SubjectId = "";
    @Input() editionId: EditionId = "";
    @Input() editingActivity = false;
    @Input() currentPerformActivityForm?: Perform;

    @Output() create = new EventEmitter<Perform>();
    @Output() update = new EventEmitter<Perform>();
    @Output() cancel = new EventEmitter<any>();

    @Output() setActivityFormValidity = new EventEmitter<boolean>();
    @Output() setPerformActivityFormData = new EventEmitter<Perform>();

    avenueForm = this.fb.group({
        description: [
            "",
            [
                Validators.required,
                this.formHelper.noQuillEditorWhitespaceValidator,
            ],
        ],
        displayName: [
            "",
            [Validators.required, this.formHelper.noWhitespaceValidator],
        ],
        levelIds: [[], [Validators.required]],
        unit: ["", []],
        publishedBy: ["", Validators.required],
        visibility: ["self", Validators.required],
        audiences: [["TCH", "STU"], Validators.required],
        editionIds: ["", [Validators.required]],
        subjectId: ["", [Validators.required]],
        assetTypeId: [assetTypeIds.PERFORM, [Validators.required]],
        isResource: [false, Validators.required],
        isAssignable: [true, Validators.required],
        isDeleted: [false, Validators.required],

        parentAssetId: ["", Validators.required],
        mediaTime: ["", [Validators.required]],
        recordingTries: ["", [Validators.required]],
        recordTime: ["", [Validators.required]],
        allowPause: [false],
        allowReset: [false],
        mediaWhileRecording: [false],
        views: ["", Validators.required],
        allowMobile: [false],
        scorePossible: [10],

        // The following form parameters can only be set by the distributor.
        // The API ignores these values for non-distributors.
        lesson: ["", []],
        bookId: ["", []],
        page: ["", []],
        activityNumber: ["", []],
    });

    avenueUnits: string[] = [];
    avenueLevels: Level[] | undefined = [];
    levelLabel = "Level";
    key = 30;
    value = "0:30";
    timeOptions: [{key: number, value: string}] = [{key: 0, value: "start"}];


    visibilityOpts: KeyValue<string, string>[] = [];

    viewsTriesOpts = [
        { key: 1, value: 1 },
        { key: 2, value: 2 },
        { key: 3, value: 3 },
        { key: 4, value: 4 },
        { key: 5, value: 5 },
        { key: 6, value: "Unlimited" },
    ];

    timeOpts = [
        { key: 30, value: "0:30" },
        { key: 60, value: "1:00" },
        { key: 90, value: "1:30" },
        { key: 120, value: "2:00" },
        { key: 180, value: "3:00" },
    ];

    constructor(
        private fb: UntypedFormBuilder,
        public localStorage: LocalStorage,
        private languageHelper: LanguageHelper,
        private formHelper: FormHelper
    ) {}

    ngOnInit() {
        this.createRecordingTimeOptions();
        this.avenueForm.patchValue({ editionIds: [this.editionId] });
        this.selectedEdition = this.activeEditions.find(ed => ed.editionId === this.editionId);
        this.levelLabel = this.languageHelper.gradeLevelReturn(this.subjectId);
        this.visibilityOpts = this.formHelper.getVisibilityOpts();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.currentPerformActivityForm) {
            this.exists = true;
            this.externalId = this.currentPerformActivityForm.externalId;
            this.performAssetId = this.currentPerformActivityForm.assetId;
            let publishedBy = this.currentPerformActivityForm.publishedBy;
            if (publishedBy == null && this.isDistributor && this.userGUID) {
                // Fix for legacy tasks that don't have an ownerID set.
                publishedBy = this.userGUID;
            }
            this.selectedAssetID = this.currentPerformActivityForm.parentAssetId;
            let views = this.currentPerformActivityForm.views;
            if (views === -1) {
                views = 6;
            }
            let recordingTries = this.currentPerformActivityForm.recordingTries;
            if (recordingTries === -1) {
                recordingTries = 6;
            }
            this.avenueForm.patchValue({
                description: this.currentPerformActivityForm.description,
                displayName: this.currentPerformActivityForm.displayName,
                publishedBy,
                levelIds: this.currentPerformActivityForm.levelIds + "",//This weird hack is for converting levelIds to a string if it isn't already. Definitely sucks.
                unit: this.currentPerformActivityForm.unit,
                visibility: this.currentPerformActivityForm.visibility,
                audiences: this.currentPerformActivityForm.audiences,
                editionIds: this.currentPerformActivityForm.editionIds,
                subjectId: this.currentPerformActivityForm.subjectId,
                assetTypeId: this.currentPerformActivityForm.assetTypeId,
                parentAssetId: this.currentPerformActivityForm.parentAssetId,
                mediaTime: this.currentPerformActivityForm.mediaTime,
                recordTime: this.currentPerformActivityForm.recordTime,
                mediaWhileRecording: this.currentPerformActivityForm.mediaWhileRecording,
                allowPause: this.currentPerformActivityForm.allowPause,
                allowReset: this.currentPerformActivityForm.allowReset,
                instructionMedia: this.currentPerformActivityForm.instructionMedia,
                recordingTries: this.mapRetriesToForm(recordingTries),
                views: views,
                lesson: this.currentPerformActivityForm.lesson,
                page: this.currentPerformActivityForm.page,
                bookId: this.currentPerformActivityForm.bookId,
                activityNumber: this.currentPerformActivityForm.activityNumber,
                allowMobile: this.currentPerformActivityForm.allowMobile,
                scorePossible: this.currentPerformActivityForm.scorePossible,
            });
            if (this.currentPerformActivityForm.subjectId) {
                this.subjectId = this.currentPerformActivityForm.subjectId;
            }
        } else {
            this.avenueForm.patchValue({
                publishedBy: this.userGUID,
                subjectId: this.subjectId,
            });
        }
        const activeProgram = this.activePrograms.find(
            (prgm) => prgm.subjectId === this.subjectId
        );
        if (activeProgram) {
            this.activeProgram = {
                ...activeProgram,
                editionId: this.editionId,
            };
        }
        this.setValidUnitsAndLevelsForSubjectId(this.subjectId);
    }

    createRecordingTimeOptions(){
        // Creates an array of the recording time options for the dropdown
        // Increases by 30 seconds until it reaches 10 minutes
        while (this.key <= 600) {
            const timeObject = {
                key: this.key,
                value: this.value,
            };
        if (this.key === 30) {
            this.timeOptions = [timeObject];
        } else {
            this.timeOptions.push(timeObject);
        }
        this.key += 30;
        const minutes = Math.floor(this.key / 60);
        const seconds = this.key % 60;
        this.value = minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
        }
    }

    setValidUnitsAndLevelsForSubjectId(subjectId = ""): void {
        const sId: SubjectId = subjectId || this.subjectId;
        this.formHelper.setSubjectAndEdition(sId, this.editionId, this.activePrograms, this.activeEditions);
        this.avenueLevels = this.formHelper.getFormLevels();// levels
        if (this.editingActivity && this.currentPerformActivityForm?.editionIds) {
            const avenueFormLevels = this.currentPerformActivityForm?.levelIds || [], allLevels = this.avenueLevels.map(l => l.levelId);
            this.avenueLevels = [];// Here is to limit the level for cloned or editing existed asset. It should not run while creating a new asset
            this.currentPerformActivityForm?.editionIds.forEach(e => {
                this.formHelper.setSubjectAndEdition(sId, e, this.activePrograms, this.activeEditions);
                const levels = this.formHelper.getFormLevelsForAssetsLevelIds(avenueFormLevels.length ? avenueFormLevels : allLevels);
                Array.prototype.push.apply(this.avenueLevels, levels);
            });
        }
        this.avenueUnits = this.formHelper.getFormUnits();// units
    }

    getAssetIDs(objectArray: Perform[]) {
        const assetIDs: AssetId[] = [];
        objectArray.forEach((asset) => {
            assetIDs.push(asset.assetId);
        });
        return assetIDs;
    }

    isMath(subjectId: SubjectId): boolean {
        return this.languageHelper.ifMathLanguage(subjectId);
    }

    submit() {
        if (this.avenueForm.valid) {
            if (!this.exists) {
                const avenueFormCopy = this.avenueForm;
                avenueFormCopy.value.recordTries = this.mapRetriesToApi(
                    avenueFormCopy.value.recordTries
                );
                this.create.emit(avenueFormCopy.value);
            } else {
                this.avenueForm.value.taskDataID = this.externalId;
                this.avenueForm.value.assetId = this.performAssetId;
                const avenueFormCopy = this.avenueForm;
                avenueFormCopy.value.recordTries = this.mapRetriesToApi(
                    avenueFormCopy.value.recordTries
                );
                this.update.emit(avenueFormCopy.value);
            }

            this.avenueForm.markAsPristine();
        }
    }

    onSelectAsset(asset: PerformMedia) {
        if (asset) {
            this.selectedAssetID = asset.assetId;
            this.avenueForm.patchValue({ parentAssetId: asset.assetId });
        } else {
            this.avenueForm.patchValue({ parentAssetId: "" });
        }
        this.onChange();
    }

    onChange() {
        const formValidity = this.avenueForm.valid;
        if (!this.componentInit && this.editingActivity) {
            this.componentInit = true;
        }
        if (this.avenueForm) {
            this.setActivityFormValidity.emit(formValidity);
            if (this.exists) {
                this.avenueForm.value.assetId = this.performAssetId;
                this.avenueForm.value.externalId = this.externalId;
            }
            this.setPerformActivityFormData.emit(this.avenueForm.value);
        }
    }

    mapRetriesToForm(value: number | undefined) {
        // value 6 is used as temp placeholder for unlimited which in the database is actually null
        // if Tries option is not selected, it will not preselected "Unlimited" 6 for user
        return value ? value : "";
    }

    mapRetriesToApi(value: number) {
        // value 6 is used as temp placeholder for unlimited which in the database is actually null
        return value === 6 ? null : value;
    }

    setQuillEditorTouched() {
        this.avenueForm.controls.description.markAsTouched();
    }
}
